import * as React from 'react'

import { Row } from '@tanstack/react-table'

import { ResourceGroupHeader } from '~/client/src/shared/components/GroupHeader'
import { SharedVirtualTable } from '~/client/src/shared/components/SharedVirtualTable'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { useResolveGroupPresentation } from '~/client/src/shared/resources/presentation/resolveGroupPresentation'

import { useResource } from '../context'
import { useResourceTableAdapter } from '../hooks/useResourceTableAdapter'
import { IResourceRowData } from '../types'

const GROUP_ROW_HEIGHT = 32
const RECORD_ROW_HEIGHT = 40

export const ResourceTable: React.FC = () => {
  const {
    state: { grouping },
    actions: { selectRecord, toggleCategorySelection, toggleCategory },
    meta: { isSelected, columns, isLoading, resourceTypeId },
  } = useResource()

  const { table } = useResourceTableAdapter()
  const resolveGroupPresentation = useResolveGroupPresentation()

  const groupingPropertyConfig = React.useMemo(
    () =>
      grouping
        ? columns.find(
            col =>
              col.dataKey === grouping || col.dataKey === `data.${grouping}`,
          )?.propertyConfig
        : undefined,
    [grouping, columns],
  )

  const handleRowClick = React.useCallback(
    (row: Row<IResourceRowData>) => {
      if (row.original.rowType === 'record' && row.original.recordId) {
        selectRecord(row.original.recordId)
      }
    },
    [selectRecord],
  )

  const renderRow = React.useCallback(
    (row: Row<IResourceRowData>, defaultCells: React.ReactNode[]) => {
      if (row.original.rowType !== 'group') {
        return defaultCells
      }

      const groupKey = row.original.groupKey || ''
      const groupPath = row.original.groupPath || groupKey
      const depth = row.original.depth ?? 0
      const groupRecordIds = row.original.groupRecordIds || []
      const selectedCount = groupRecordIds.reduce(
        (count, id) => (isSelected(id) ? count + 1 : count),
        0,
      )
      const isChecked =
        groupRecordIds.length > 0 && selectedCount === groupRecordIds.length
      const isPartialChecked = selectedCount > 0 && !isChecked
      const isCollapsed = !!row.original.isGroupCollapsed

      const presentation = resolveGroupPresentation({
        propertyConfig: groupingPropertyConfig,
        groupKey,
        count: groupRecordIds.length,
        propertyName: grouping,
        resourceTypeId,
      })

      return (
        <div
          role="gridcell"
          aria-colindex={1}
          aria-colspan={table.getVisibleLeafColumns().length}
          className="tw:relative tw:flex tw:items-center tw:border-0 tw:border-b tw:border-solid tw:border-input-soft tw:bg-sidebar tw:select-none"
          style={{
            minHeight: GROUP_ROW_HEIGHT,
            width: table.getTotalSize(),
            minWidth: table.getTotalSize(),
            maxWidth: table.getTotalSize(),
          }}
          onClick={event => {
            // The band owns its own toggle interactions (trigger button,
            // selection checkbox). This handler only fires for clicks in
            // the gridcell's empty space beyond the sticky-left band.
            const target = event.target as HTMLElement
            if (target.closest('[data-slot="resource-group-band"]')) return
            event.stopPropagation()
            toggleCategory(groupPath)
          }}
        >
          <ResourceGroupHeader
            label={row.original.groupLabel || groupKey}
            count={groupRecordIds.length}
            depth={depth}
            isExpanded={!isCollapsed}
            onToggle={() => toggleCategory(groupPath)}
            presentation={presentation}
            // pl: row checkbox is centred in the 40px column's px-3 content box (15px after the
            // 1px border-r) → 12 + (15 - 16) / 2.
            className="tw:sticky tw:left-0 tw:z-[1] tw:w-fit tw:max-w-full tw:border-b-0 tw:pl-[11.5px]"
            selection={{
              checked: isChecked,
              indeterminate: isPartialChecked,
              onChange: () => toggleCategorySelection(groupRecordIds),
              ariaLabel: `Select group ${row.original.groupLabel || groupKey}`,
            }}
          />
        </div>
      )
    },
    [
      grouping,
      groupingPropertyConfig,
      isSelected,
      resolveGroupPresentation,
      resourceTypeId,
      table,
      toggleCategory,
      toggleCategorySelection,
    ],
  )

  if (isLoading) {
    return (
      <div className="tw:flex tw:flex-1 tw:min-h-0 tw:items-center tw:justify-center">
        <Spinner size="md" />
      </div>
    )
  }

  return (
    <SharedVirtualTable
      table={table}
      ariaLabel="Resource table"
      onRowClick={handleRowClick}
      renderRow={renderRow}
      estimateRowHeight={row =>
        row.original.rowType === 'group' ? GROUP_ROW_HEIGHT : RECORD_ROW_HEIGHT
      }
      getRowClassName={row =>
        row.original.rowType === 'group' ? 'tw:cursor-pointer' : undefined
      }
    />
  )
}
